import { 
  doc, 
  getDoc, 
  setDoc,
  updateDoc,
  Timestamp,
  deleteField
} from 'firebase/firestore';
import { db } from '../firebase/config';
import {
  ItemCard,
  ItemCardSetDocument,
  ItemCardCreateData,
  ItemCardUpdateData,
  ItemCardCategory,
  StudentItemCard
} from '../types/itemCard';
import { ClassMembersPartitionService } from './classMembersPartitionService';

/** V2 듀얼 읽기: levelupItem/assignmentItem/storeItem (배열 또는 객체) → ItemCard[] */
function itemCardFieldToArray(raw: any): ItemCard[] {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.map((item: any) => ({ ...item, cardImage: item.cardImage || 'default' }));
  }
  if (typeof raw === 'object') {
    return Object.values(raw).map((item: any) => ({ ...item, cardImage: item.cardImage || 'default' }));
  }
  return [];
}

export class ItemCardService {
  private userId: string;
  private classId: string;
  private memberGroup?: string;
  private classMembersService: ClassMembersPartitionService;

  constructor(userId: string, classId: string, memberGroup?: string) {
    this.userId = userId;
    this.classId = classId;
    this.memberGroup = memberGroup;
    this.classMembersService = new ClassMembersPartitionService(userId, classId, memberGroup);
  }

  // 아이템 카드 세트 문서 경로
  private getItemCardSetDocPath(): string {
    return `users/${this.userId}/classes/${this.classId}/setCollection/itemCardSet`;
  }

  // 아이템 카드 세트 로드
  async loadItemCardSet(): Promise<ItemCardSetDocument | null> {
    try {
      const docRef = doc(db, this.getItemCardSetDocPath());
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        const levelupItems = itemCardFieldToArray(data.levelupItem);
        const assignmentItems = itemCardFieldToArray(data.assignmentItem);
        const storeItems = itemCardFieldToArray(data.storeItem);
        return {
          levelupItem: levelupItems,
          assignmentItem: assignmentItems,
          storeItem: storeItems,
          userCardImageOrder: data.userCardImageOrder || [],
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date()
        };
      }

      // 문서가 없으면 초기화
      return await this.initializeItemCardSet();
    } catch (error) {
      console.error('아이템 카드 세트 로드 실패:', error);
      throw error;
    }
  }

  // 아이템 카드 세트 초기화
  private async initializeItemCardSet(): Promise<ItemCardSetDocument> {
    const initialData: ItemCardSetDocument = {
      levelupItem: [],
      assignmentItem: [],
      storeItem: [],
      createdAt: new Date(),
      updatedAt: new Date()
    };

    const docRef = doc(db, this.getItemCardSetDocPath());
    await setDoc(docRef, {
      levelupItem: [],
      assignmentItem: [],
      storeItem: [],
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });

    return initialData;
  }

  // 아이템 카드 추가
  async addItemCard(data: ItemCardCreateData): Promise<ItemCard> {
    try {
      const itemCardSet = await this.loadItemCardSet();
      if (!itemCardSet) throw new Error('아이템 카드 세트를 로드할 수 없습니다.');

      const newItemCard: ItemCard = {
        itemCardId: `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name: data.name,
        memo: data.memo,
        type: data.type,
        useCount: data.type === 'active' ? data.useCount : undefined,
        cost: data.cost,
        category: data.category,
        icon: data.icon,
        cardImage: data.cardImage || 'default',
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const targetField = data.category === 'levelup' ? 'levelupItem' : 
                          data.category === 'assignment' ? 'assignmentItem' : 'storeItem';
      const docRef = doc(db, this.getItemCardSetDocPath());
      const rawSnap = await getDoc(docRef);
      const rawData = rawSnap.exists() ? rawSnap.data() : null;
      const isObjectFormat = rawData && typeof rawData[targetField] === 'object' && !Array.isArray(rawData[targetField]);

      const cleanItemForFirestore = (item: ItemCard) => {
        const firestoreItem: any = {
          itemCardId: item.itemCardId,
          name: item.name,
          memo: item.memo,
          type: item.type,
          cost: item.cost,
          category: item.category,
          createdAt: item.createdAt instanceof Date ? Timestamp.fromDate(item.createdAt) : item.createdAt,
          updatedAt: item.updatedAt instanceof Date ? Timestamp.fromDate(item.updatedAt) : item.updatedAt
        };
        if (item.useCount !== undefined) firestoreItem.useCount = item.useCount;
        if (item.icon !== undefined && item.icon !== null) firestoreItem.icon = item.icon;
        if (item.cardImage !== undefined && item.cardImage !== null) firestoreItem.cardImage = item.cardImage;
        return firestoreItem;
      };

      const firestoreItem = cleanItemForFirestore(newItemCard);

      if (isObjectFormat) {
        await updateDoc(docRef, {
          [`${targetField}.${newItemCard.itemCardId}`]: firestoreItem,
          updatedAt: Timestamp.now()
        });
      } else {
        const updatedArray = [...itemCardSet[targetField], newItemCard];
        await setDoc(docRef, {
          [targetField]: updatedArray.map(cleanItemForFirestore),
          updatedAt: Timestamp.now()
        }, { merge: true });
      }

      return newItemCard;
    } catch (error) {
      console.error('아이템 카드 추가 실패:', error);
      throw error;
    }
  }

  // 아이템 카드 수정
  async updateItemCard(data: ItemCardUpdateData): Promise<void> {
    try {
      const itemCardSet = await this.loadItemCardSet();
      if (!itemCardSet) throw new Error('아이템 카드 세트를 로드할 수 없습니다.');

      // 레벨업 아이템에서 찾기
      let found = false;
      let category: ItemCardCategory = 'levelup';

      const updatedLevelupItems = itemCardSet.levelupItem.map(item => {
        if (item.itemCardId === data.itemCardId) {
          found = true;
          return {
            ...item,
            ...data,
            updatedAt: new Date()
          };
        }
        return item;
      });

      // 과제보상 아이템에서 찾기
      const updatedAssignmentItems = itemCardSet.assignmentItem.map(item => {
        if (item.itemCardId === data.itemCardId) {
          found = true;
          category = 'assignment';
          return {
            ...item,
            ...data,
            updatedAt: new Date()
          };
        }
        return item;
      });

      // 스토어 아이템에서 찾기
      const updatedStoreItems = itemCardSet.storeItem.map(item => {
        if (item.itemCardId === data.itemCardId) {
          found = true;
          category = 'store';
          return {
            ...item,
            ...data,
            updatedAt: new Date()
          };
        }
        return item;
      });

      if (!found) {
        throw new Error('아이템 카드를 찾을 수 없습니다.');
      }

      const docRef = doc(db, this.getItemCardSetDocPath());
      const rawSnap = await getDoc(docRef);
      const rawData = rawSnap.exists() ? rawSnap.data() : null;
      const targetField = category === 'levelup' ? 'levelupItem' : category === 'assignment' ? 'assignmentItem' : 'storeItem';
      const isObjectFormat = rawData && typeof rawData[targetField] === 'object' && !Array.isArray(rawData[targetField]);

      const cleanItemForFirestore = (item: ItemCard) => {
        const firestoreItem: any = {
          itemCardId: item.itemCardId,
          name: item.name,
          memo: item.memo,
          type: item.type,
          cost: item.cost,
          category: item.category,
          createdAt: item.createdAt instanceof Date ? Timestamp.fromDate(item.createdAt) : item.createdAt,
          updatedAt: item.updatedAt instanceof Date ? Timestamp.fromDate(item.updatedAt) : item.updatedAt
        };
        if (item.useCount !== undefined) firestoreItem.useCount = item.useCount;
        if (item.icon !== undefined && item.icon !== null) firestoreItem.icon = item.icon;
        if (item.cardImage !== undefined && item.cardImage !== null) firestoreItem.cardImage = item.cardImage;
        return firestoreItem;
      };

      const updatedItem = category === 'levelup' ? updatedLevelupItems.find(i => i.itemCardId === data.itemCardId)
        : category === 'assignment' ? updatedAssignmentItems.find(i => i.itemCardId === data.itemCardId)
        : updatedStoreItems.find(i => i.itemCardId === data.itemCardId);

      if (isObjectFormat && updatedItem) {
        await updateDoc(docRef, {
          [`${targetField}.${data.itemCardId}`]: cleanItemForFirestore(updatedItem),
          updatedAt: Timestamp.now()
        });
      } else {
        await setDoc(docRef, {
          levelupItem: updatedLevelupItems.map(cleanItemForFirestore),
          assignmentItem: updatedAssignmentItems.map(cleanItemForFirestore),
          storeItem: updatedStoreItems.map(cleanItemForFirestore),
          updatedAt: Timestamp.now()
        }, { merge: true });
      }
    } catch (error) {
      console.error('아이템 카드 수정 실패:', error);
      throw error;
    }
  }

  /**
   * 아이템 카드 세트 전체 저장 (추가/수정/삭제/순서를 한 번에 반영)
   * temp_ 아이템은 실제 id로 생성하여 저장
   */
  async saveItemCardSet(
    levelupItem: ItemCard[],
    assignmentItem: ItemCard[],
    storeItem: ItemCard[]
  ): Promise<void> {
    const cleanItemForFirestore = (item: ItemCard) => {
      const firestoreItem: any = {
        itemCardId: item.itemCardId,
        name: item.name,
        memo: item.memo,
        type: item.type,
        cost: item.cost,
        category: item.category,
        createdAt: item.createdAt instanceof Date ? Timestamp.fromDate(item.createdAt) : item.createdAt,
        updatedAt: item.updatedAt instanceof Date ? Timestamp.fromDate(item.updatedAt) : item.updatedAt
      };
      if (item.useCount !== undefined) firestoreItem.useCount = item.useCount;
      if (item.icon !== undefined && item.icon !== null) firestoreItem.icon = item.icon;
      if (item.cardImage !== undefined && item.cardImage !== null) firestoreItem.cardImage = item.cardImage;
      return firestoreItem;
    };

    const ensureRealId = (item: ItemCard, index: number): ItemCard => {
      if (item.itemCardId.startsWith('temp_')) {
        return {
          ...item,
          itemCardId: `item_${Date.now()}_${index}_${Math.random().toString(36).substr(2, 9)}`,
          updatedAt: new Date()
        };
      }
      return item;
    };

    const levelup = levelupItem.map((item, i) => ensureRealId(item, i)).map(cleanItemForFirestore);
    const assignment = assignmentItem.map((item, i) => ensureRealId(item, i)).map(cleanItemForFirestore);
    const store = storeItem.map((item, i) => ensureRealId(item, i)).map(cleanItemForFirestore);

    const docRef = doc(db, this.getItemCardSetDocPath());
    const rawSnap = await getDoc(docRef);
    const rawData = rawSnap.exists() ? rawSnap.data() : null;

    await setDoc(docRef, {
      levelupItem: levelup,
      assignmentItem: assignment,
      storeItem: store,
      ...(rawData?.userCardImageOrder && { userCardImageOrder: rawData.userCardImageOrder }),
      updatedAt: Timestamp.now()
    }, { merge: true });
  }

  // 아이템 카드 삭제 (V2: deleteField 지원)
  async deleteItemCard(itemCardId: string, category: ItemCardCategory): Promise<void> {
    try {
      const itemCardSet = await this.loadItemCardSet();
      if (!itemCardSet) throw new Error('아이템 카드 세트를 로드할 수 없습니다.');

      const targetField = category === 'levelup' ? 'levelupItem' : 
                          category === 'assignment' ? 'assignmentItem' : 'storeItem';
      const docRef = doc(db, this.getItemCardSetDocPath());
      const rawSnap = await getDoc(docRef);
      const rawData = rawSnap.exists() ? rawSnap.data() : null;
      const isObjectFormat = rawData && typeof rawData[targetField] === 'object' && !Array.isArray(rawData[targetField]);

      if (isObjectFormat) {
        await updateDoc(docRef, {
          [`${targetField}.${itemCardId}`]: deleteField(),
          updatedAt: Timestamp.now()
        });
        return;
      }

      const updatedArray = itemCardSet[targetField].filter(item => item.itemCardId !== itemCardId);
      const cleanItemForFirestore = (item: ItemCard) => {
        const firestoreItem: any = {
          itemCardId: item.itemCardId,
          name: item.name,
          memo: item.memo,
          type: item.type,
          cost: item.cost,
          category: item.category,
          createdAt: item.createdAt instanceof Date ? Timestamp.fromDate(item.createdAt) : item.createdAt,
          updatedAt: item.updatedAt instanceof Date ? Timestamp.fromDate(item.updatedAt) : item.updatedAt
        };
        if (item.useCount !== undefined) firestoreItem.useCount = item.useCount;
        if (item.icon !== undefined && item.icon !== null) firestoreItem.icon = item.icon;
        if (item.cardImage !== undefined && item.cardImage !== null) firestoreItem.cardImage = item.cardImage;
        return firestoreItem;
      };
      await setDoc(docRef, {
        [targetField]: updatedArray.map(cleanItemForFirestore),
        updatedAt: Timestamp.now()
      }, { merge: true });
    } catch (error) {
      console.error('아이템 카드 삭제 실패:', error);
      throw error;
    }
  }

  // 카테고리별 아이템 카드 로드
  async loadItemCardsByCategory(category: ItemCardCategory): Promise<ItemCard[]> {
    try {
      const itemCardSet = await this.loadItemCardSet();
      if (!itemCardSet) return [];

      return category === 'levelup' ? itemCardSet.levelupItem : itemCardSet.storeItem;
    } catch (error) {
      console.error('카테고리별 아이템 카드 로드 실패:', error);
      return [];
    }
  }

  // 특정 아이템 카드 조회
  async getItemCard(itemCardId: string): Promise<ItemCard | null> {
    try {
      const itemCardSet = await this.loadItemCardSet();
      if (!itemCardSet) return null;

      const allItems = [...itemCardSet.levelupItem, ...itemCardSet.assignmentItem, ...itemCardSet.storeItem];
      return allItems.find(item => item.itemCardId === itemCardId) || null;
    } catch (error) {
      console.error('아이템 카드 조회 실패:', error);
      return null;
    }
  }

  // 멤버의 아이템 카드 목록 조회 (ClassMembers 문서에서)
  async getStudentItemCards(studentId: string): Promise<StudentItemCard[]> {
    try {
      const allMembers = await this.classMembersService.loadAllMembers();
      const student = allMembers.find(member => member.id === studentId);
      
      if (!student || !student.itemCards) {
        return [];
      }

      const normalizeAcquiredAt = (value: any): Date => {
        if (!value) return new Date();
        if (value instanceof Date) return value;
        if (typeof value?.toDate === 'function') return value.toDate();
        if (typeof value === 'string' || typeof value === 'number') {
          const parsed = new Date(value);
          return isNaN(parsed.getTime()) ? new Date() : parsed;
        }
        return new Date();
      };

      // 데이터 마이그레이션: category 필드가 없는 경우 기본값 설정
      return student.itemCards.map((item: any) => ({
        ...item,
        acquiredAt: normalizeAcquiredAt(item.acquiredAt),
        category: item.category || (item.assignmentId ? 'assignment' : 'levelup')
      }));
    } catch (error) {
      console.error('멤버 아이템 카드 조회 실패:', error);
      return [];
    }
  }

  // 멤버에게 아이템 카드 추가 (ClassMembers 문서에)
  async addStudentItemCard(studentId: string, studentItemCard: StudentItemCard): Promise<void> {
    try {
      // 현재 멤버의 아이템 카드 목록 가져오기
      const existingItemCards = await this.getStudentItemCards(studentId);
      
      // 새 아이템 카드 추가
      const updatedItemCards = [...existingItemCards, studentItemCard];

      // ClassMembers 문서에서 해당 멤버의 itemCards 업데이트
      await this.classMembersService.updateStudentItemCards(studentId, updatedItemCards);

      console.log('✅ 멤버 아이템 카드 추가 완료:', {
        studentId,
        itemCardId: studentItemCard.itemCardId,
        assignmentId: studentItemCard.assignmentId
      });
    } catch (error) {
      console.error('❌ 멤버 아이템 카드 추가 실패:', error);
      throw error;
    }
  }

  /**
   * 멤버에게 아이템 카드 1장 추가 (동시 지급 시 덮어쓰기 방지)
   * - 과제 완료 보상·스토어 등 동시 요청이 있을 때 사용 권장
   */
  async addStudentItemCardAtomic(studentId: string, studentItemCard: StudentItemCard): Promise<void> {
    await this.classMembersService.addStudentItemCardAtomic(studentId, studentItemCard);
    console.log('✅ 멤버 아이템 카드 추가 완료 (atomic):', {
      studentId,
      itemCardId: studentItemCard.itemCardId,
      assignmentId: studentItemCard.assignmentId
    });
  }

  // 특정 과제로부터 받은 아이템 카드들 제거 (ClassMembers 문서에서)
  async removeStudentItemCardsByAssignment(studentId: string, assignmentId: string): Promise<void> {
    try {
      // 현재 멤버의 아이템 카드 목록 가져오기
      const existingItemCards = await this.getStudentItemCards(studentId);

      // 해당 과제로부터 받은 아이템 카드들 제거
      const filteredItemCards = existingItemCards.filter(
        (item: any) => item.assignmentId !== assignmentId
      );

      // ClassMembers 문서에서 해당 멤버의 itemCards 업데이트
      await this.classMembersService.updateStudentItemCards(studentId, filteredItemCards);

      console.log('✅ 과제 아이템 카드 제거 완료:', {
        studentId,
        assignmentId,
        removedCount: existingItemCards.length - filteredItemCards.length
      });
    } catch (error) {
      console.error('❌ 과제 아이템 카드 제거 실패:', error);
      throw error;
    }
  }

  // 특정 과제로부터 받은 아이템 카드들 잠금 처리 (ClassMembers 문서에서)
  async lockStudentItemCardsByAssignment(studentId: string, assignmentId: string): Promise<void> {
    try {
      // 현재 멤버의 아이템 카드 목록 가져오기
      const existingItemCards = await this.getStudentItemCards(studentId);

      // 해당 과제로부터 받은 아이템 카드들 잠금 처리
      // 과제 미완료 시에는 해당 과제 아이템만 잠금 처리
      // 다른 조건(레벨업 등)으로 받은 아이템은 그대로 유지
      const updatedItemCards = existingItemCards.map((item: any) => {
        if (item.assignmentId === assignmentId && !item.isLocked) {
          return {
            ...item,
            isLocked: true
          };
        }
        return item;
      });

      // ClassMembers 문서에서 해당 멤버의 itemCards 업데이트
      await this.classMembersService.updateStudentItemCards(studentId, updatedItemCards);

      // 캐시 무효화 - 아이템 카드 상태 변경 즉시 반영
      try {
        const { userDataService } = await import('../services/userDataService');
        userDataService.invalidateMembersCache(this.userId, this.classId, this.memberGroup);
      } catch (error) {
        console.warn('아이템 카드 잠금 후 캐시 무효화 실패 (무시):', error);
      }

    } catch (error) {
      console.error('❌ 과제 아이템 카드 잠금 실패:', error);
      throw error;
    }
  }

  // 특정 과제로부터 받은 아이템 카드들 잠금 해제 (ClassMembers 문서에서)
  async unlockStudentItemCardsByAssignment(studentId: string, assignmentId: string): Promise<void> {
    try {
      // 현재 멤버의 아이템 카드 목록 가져오기
      const existingItemCards = await this.getStudentItemCards(studentId);

      // 해당 과제로부터 받은 아이템 카드들 잠금 해제
      // 과제 완료 시에는 해당 과제의 아이템뿐만 아니라 모든 잠금 상태를 해제하여
      // 레벨업 보상 등 다른 조건으로 잠금된 아이템도 사용할 수 있도록 함
      const updatedItemCards = existingItemCards.map((item: any) => {
        if (item.assignmentId === assignmentId) {
          // 과제 아이템은 무조건 잠금 해제
          return {
            ...item,
            isLocked: false
          };
        } else if (item.isLocked && item.category === 'assignment') {
          // 다른 과제 아이템도 잠금 해제 (동시에 여러 과제 완료 가능)
          return {
            ...item,
            isLocked: false
          };
        }
        return item;
      });

      // ClassMembers 문서에서 해당 멤버의 itemCards 업데이트
      await this.classMembersService.updateStudentItemCards(studentId, updatedItemCards);

      // 캐시 무효화 - 아이템 카드 상태 변경 즉시 반영
      try {
        const { userDataService } = await import('../services/userDataService');
        userDataService.invalidateMembersCache(this.userId, this.classId, this.memberGroup);
      } catch (error) {
        console.warn('아이템 카드 잠금 해제 후 캐시 무효화 실패 (무시):', error);
      }

    } catch (error) {
      console.error('❌ 과제 아이템 카드 잠금 해제 실패:', error);
      throw error;
    }
  }

  // 특정 아이템 카드가 학생들에게 지급되었는지 확인
  async isItemCardGrantedToStudents(itemCardId: string): Promise<boolean> {
    try {
      const allMembers = await this.classMembersService.loadAllMembers();
      
      // 모든 학생의 itemCards를 확인하여 해당 itemCardId가 있는지 체크
      for (const member of allMembers) {
        if (member.itemCards && member.itemCards.length > 0) {
          const hasItemCard = member.itemCards.some(
            (item: any) => item.itemCardId === itemCardId
          );
          if (hasItemCard) {
            return true; // 하나라도 지급된 경우 true 반환
          }
        }
      }
      
      return false; // 지급된 학생이 없으면 false
    } catch (error) {
      console.error('❌ 아이템 카드 지급 여부 확인 실패:', error);
      // 에러 발생 시 안전하게 true 반환 (삭제 방지)
      return true;
    }
  }

  // 사용자 카드 이미지 순서 저장 (null 포함한 전체 40개 슬롯 배열)
  async saveUserCardImageOrder(imageOrder: (string | null)[]): Promise<void> {
    try {
      const docRef = doc(db, this.getItemCardSetDocPath());
      console.log('📝 저장 경로:', this.getItemCardSetDocPath());
      console.log('📝 저장할 순서:', imageOrder.length, '개 슬롯 (null 포함)', imageOrder);
      
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        // 문서가 있으면 updateDoc으로 업데이트
        console.log('📝 기존 문서 업데이트');
        await updateDoc(docRef, {
          userCardImageOrder: imageOrder,
          updatedAt: Timestamp.now()
        });
      } else {
        // 문서가 없으면 초기화 후 저장
        console.log('📝 새 문서 생성');
        await this.initializeItemCardSet();
        await updateDoc(docRef, {
          userCardImageOrder: imageOrder,
          updatedAt: Timestamp.now()
        });
      }
      console.log('✅ 사용자 카드 이미지 순서 저장 완료');
    } catch (error) {
      console.error('❌ 사용자 카드 이미지 순서 저장 실패:', error);
      throw error;
    }
  }

  // 사용자 카드 이미지 순서 로드 (null 포함한 전체 40개 슬롯 배열)
  async loadUserCardImageOrder(): Promise<(string | null)[]> {
    try {
      const itemCardSet = await this.loadItemCardSet();
      const savedOrder = itemCardSet?.userCardImageOrder;
      
      // 저장된 순서가 있으면 그대로 반환 (null 포함)
      if (savedOrder && Array.isArray(savedOrder)) {
        // 40개 슬롯으로 정규화 (부족하면 null로 채움)
        const normalized: (string | null)[] = Array(40).fill(null);
        savedOrder.forEach((url, idx) => {
          if (idx < 40) {
            normalized[idx] = url || null;
          }
        });
        return normalized;
      }
      
      // 저장된 순서가 없으면 빈 배열 반환
      return Array(40).fill(null);
    } catch (error) {
      console.error('❌ 사용자 카드 이미지 순서 로드 실패:', error);
      return Array(40).fill(null);
    }
  }
}

